import { Analytics } from '@util/Analytics';
import { recordError } from '@util/Errors';
import React, { ReactElement, useEffect } from 'react';
import { pick } from 'ts-utils';
import { LayoutScreenConfig, ScreenType, SharedScreenName } from '../../../config/FlowTypes';
import { useAppStore } from '../../../store/Store';
import { dispatchUserData } from '../../../store/userData/UserDataActions';
import { checkMedicareAccessEligibility } from '../../../store/userData/effects/CheckMedicareAccessEligibility';
import { isExcludedFromMedicareAccess } from '../../../util/MedicareAccess';
import { getMedicareAccessStage } from '../../../util/MedicareAccessSymptomUtils';
import { NavigationProps } from '../FlowNavigation';
import {
  MedicareMbiLayout,
  MedicareMbiLayoutConfig,
  MedicareMbiSubmission,
} from '../layouts/MedicareMbiLayout';

export type MedicareAccessMbiScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.MedicareAccessMbi,
  MedicareMbiLayoutConfig,
  ScreenNames
>;

type Props<ScreenNames extends string> = NavigationProps<
  MedicareAccessMbiScreenConfig<ScreenNames>,
  ScreenNames
>;

export const MedicareAccessMbiScreen = <ScreenNames extends string>(
  props: Props<ScreenNames>
): ReactElement => {
  const excludedFromMedicareAccess = useAppStore(isExcludedFromMedicareAccess);
  const prefill = useAppStore((state) =>
    pick(state.userData, [
      'legalGivenName',
      'legalFamilyName',
      'residentialAddress',
      'medicareBeneficiaryId',
    ])
  );

  useEffect(function trackPromsStarted() {
    Analytics.track({ event: Analytics.Event.o_proms_started, period: getMedicareAccessStage() });
  }, []);

  useEffect(
    function navigateToPaymentWhenExcluded() {
      if (excludedFromMedicareAccess) {
        props.navigation.goTo(SharedScreenName.Payment as ScreenNames);
      }
    },
    [excludedFromMedicareAccess]
  );

  function onSubmit(submission: MedicareMbiSubmission): void {
    if (excludedFromMedicareAccess) {
      return;
    }

    const { givenName, familyName, residentialAddress, mbi } = submission;
    dispatchUserData({
      legalGivenName: givenName,
      legalFamilyName: familyName,
      residentialAddress,
      medicareBeneficiaryId: mbi,
    });

    // Start the eligibility check now; its lifecycleStatus result (written to the Medicare Access
    // track doc) is read later by the coverage-loading screen and o-proms-completed. Fire-and-forget
    // so the PROMs aren't blocked — an API failure leaves the track in pending_eligibility.
    void checkMedicareAccessEligibility().catch(recordError);

    // MBI + name + address are PII — never include the answer payload in analytics.
    Analytics.track({
      event: Analytics.Event.o_question_answered,
      question: props.screenName,
    });

    props.navigation.next(props);
  }

  return (
    <MedicareMbiLayout
      {...props.layout}
      prefill={{
        givenName: prefill.legalGivenName ?? props.layout.prefill?.givenName,
        familyName: prefill.legalFamilyName ?? props.layout.prefill?.familyName,
        residentialAddress: prefill.residentialAddress ?? props.layout.prefill?.residentialAddress,
        mbi: prefill.medicareBeneficiaryId ?? props.layout.prefill?.mbi,
      }}
      onPressPrimaryButton={onSubmit}
    />
  );
};
